import slugify from "./slugify";

/**
 * Normalize category for URL (convert to slug) by removing diacritics and handling special patterns like age ranges
 */
export function normalizeCategoryForUrl(category: string): string {
  let normalized = category;
  // Replace age pattern "2-5 ani" with "varsta-2-5" to keep URLs stable
  const agePattern = /(\d+)-(\d+)\s+ani/;
  normalized = normalized.replace(agePattern, "varsta-$1-$2");

  // Use slugify to remove diacritics and produce URL-friendly string
  const slug = slugify(normalized);
  return encodeURIComponent(slug);
}

/**
 * Normalize text by removing diacritics for better search experience
 * This allows searching with or without Romanian diacritics
 */
export function normalizeText(text: string): string {
  if (!text || typeof text !== "string") {
    return "";
  }
  // Lowercase and strip common punctuation. Use Unicode NFD
  // normalization to remove diacritics where needed and rely on
  // Fuse's `ignoreDiacritics` for search matching.
  return text
    .toLowerCase()
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Highlight text with query terms
 * Note: Text is assumed to come from trusted source (our JSON files)
 * and is HTML-escaped before highlighting
 */
export function highlightText(text: string, query: string): string {
  if (!text || typeof text !== "string") return "";
  if (!query || query.trim().length === 0) return escapeHtml(text);
  const normalizedQueryWords = normalizeText(query)
    .split(/\s+/)
    .filter((w) => w.length > 0);

  if (normalizedQueryWords.length === 0) return escapeHtml(text);

  // Build normalized characters using Unicode NFD and strip combining marks
  const punctuationRegex = /[.,\/#!$%\^&\*;:{}=\-_`~()]/;
  const orig = text;
  const normalizedChars: string[] = [];
  const mapNormToOrig: number[] = [];

  for (let i = 0; i < orig.length; i++) {
    const ch = orig[i];
    if (punctuationRegex.test(ch)) continue;

    const n = ch.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    normalizedChars.push(n);
    mapNormToOrig.push(i);
  }

  const normalizedText = normalizedChars.join("");

  const ranges: Array<{ start: number; end: number }> = [];

  for (const w of normalizedQueryWords) {
    if (w.length <= 1) continue;
    let startIndex = 0;
    while (true) {
      const idx = normalizedText.indexOf(w, startIndex);
      if (idx === -1) break;
      const origStart = mapNormToOrig[idx];
      const origEndIndex = idx + w.length - 1;
      const origEnd = mapNormToOrig[origEndIndex] + 1;
      ranges.push({ start: origStart, end: origEnd });
      startIndex = idx + w.length;
    }
  }

  if (ranges.length === 0) return escapeHtml(text);

  // Merge overlapping ranges
  ranges.sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (!last || r.start > last.end) merged.push({ start: r.start, end: r.end });
    else last.end = Math.max(last.end, r.end);
  }

  // Build HTML by escaping non-matched segments and wrapping matches with <strong>
  let result = "";
  let lastIndex = 0;
  for (const r of merged) {
    if (r.start > lastIndex) {
      result += escapeHtml(orig.substring(lastIndex, r.start));
    }
    result += "<strong>" + escapeHtml(orig.substring(r.start, r.end)) + "</strong>";
    lastIndex = r.end;
  }
  if (lastIndex < orig.length) {
    result += escapeHtml(orig.substring(lastIndex));
  }

  return result;
}

export function escapeHtml(text: string): string {
  if (!text || typeof text !== "string") {
    return "";
  }
  // Properly escape HTML entities in the correct order
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
